import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { KONDISI_META, fmtLuas } from "@/components/AreaView";
import { formatTanggal } from "@/lib/dates";
import AsetFotoStrip from "@/components/AsetFotoStrip";
import { Plus, Building2, Home, Pencil, Trash2, Loader2, X, History } from "lucide-react";

const KONDISI_OPTIONS = ["BAIK", "RUSAK RINGAN", "RUSAK BERAT"];
const emptyAset = { nama: "", jenis: "GEDUNG", luas_indoor: "", luas_outdoor: "", kondisi: "BAIK" };

export default function KawasanDetailDialog({ open, onOpenChange, kawasan, onChanged }) {
  const [asetList, setAsetList] = useState(null);
  const [form, setForm] = useState(null); // null = form tertutup
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyId, setHistoryId] = useState(null);

  const load = () => {
    if (!kawasan) return;
    api.get(`/kawasan/${kawasan.id}/aset`).then((res) => setAsetList(res.data)).catch(() => setAsetList([]));
  };
  useEffect(() => {
    if (open && kawasan) { setAsetList(null); setForm(null); setEditingId(null); load(); }
  }, [open, kawasan]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!kawasan) return null;

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openAdd = () => { setForm(emptyAset); setEditingId(null); };
  const openEdit = (a) => {
    setForm({ nama: a.nama, jenis: a.jenis, luas_indoor: a.luas_indoor || "", luas_outdoor: a.luas_outdoor || "", kondisi: a.kondisi });
    setEditingId(a.id);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        kawasan_id: kawasan.id,
        nama: form.nama.trim(),
        jenis: form.jenis,
        luas_indoor: Number(form.luas_indoor) || 0,
        luas_outdoor: Number(form.luas_outdoor) || 0,
        kondisi: form.kondisi,
      };
      if (editingId) {
        await api.put(`/aset/${editingId}`, payload);
        toast.success("Aset diperbarui");
      } else {
        await api.post("/aset", payload);
        toast.success("Aset ditambahkan");
      }
      setForm(null);
      setEditingId(null);
      load();
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal menyimpan aset");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/aset/${a.id}`);
      toast.success("Aset dihapus");
      load();
      onChanged();
    } catch {
      toast.error("Gagal menghapus aset");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kawasan-detail-dialog">
        <DialogHeader>
          <DialogTitle data-testid="kawasan-detail-nama">{kawasan.nama}</DialogTitle>
          <DialogDescription>
            Indoor {fmtLuas(kawasan.luas_indoor)} &middot; Outdoor {fmtLuas(kawasan.luas_outdoor)} &middot; Kondisi kawasan: {kawasan.kondisi}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Gedung & Rumah Dinas</h4>
          <Button size="sm" variant="outline" onClick={openAdd} data-testid="add-aset-button"
            className="gap-1.5 h-8 border-blue-200 text-blue-700 hover:bg-blue-50">
            <Plus className="h-3.5 w-3.5" /> Tambah Aset
          </Button>
        </div>

        {form && (
          <form onSubmit={submit} className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 space-y-3"
            data-testid="aset-form">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">{editingId ? "Ubah Aset" : "Aset Baru"}</p>
              <button type="button" onClick={() => { setForm(null); setEditingId(null); }}
                data-testid="close-aset-form" className="text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="aset-nama">Nama *</Label>
                <Input id="aset-nama" required value={form.nama} onChange={setField("nama")}
                  placeholder="Gedung A / Rumah Dinas 1" data-testid="aset-form-nama" className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label>Jenis</Label>
                <Select value={form.jenis} onValueChange={(v) => setForm((f) => ({ ...f, jenis: v }))}>
                  <SelectTrigger data-testid="aset-form-jenis" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GEDUNG">Gedung</SelectItem>
                    <SelectItem value="RUMAH_DINAS">Rumah Dinas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="aset-indoor">Luas Indoor (m²)</Label>
                <Input id="aset-indoor" type="number" min="0" step="any" value={form.luas_indoor}
                  onChange={setField("luas_indoor")} placeholder="0" data-testid="aset-form-indoor" className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="aset-outdoor">Luas Outdoor (m²)</Label>
                <Input id="aset-outdoor" type="number" min="0" step="any" value={form.luas_outdoor}
                  onChange={setField("luas_outdoor")} placeholder="0" data-testid="aset-form-outdoor" className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label>Kondisi</Label>
                <Select value={form.kondisi} onValueChange={(v) => setForm((f) => ({ ...f, kondisi: v }))}>
                  <SelectTrigger data-testid="aset-form-kondisi" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KONDISI_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={saving} data-testid="submit-aset-form"
                  className="gap-2 bg-blue-600 hover:bg-blue-700 text-white w-full">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan Aset
                </Button>
              </div>
            </div>
          </form>
        )}

        {asetList === null ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>
        ) : asetList.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center" data-testid="aset-empty">
            Belum ada gedung atau rumah dinas di kawasan ini.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="aset-list">
            {asetList.map((a) => (
              <li key={a.id} className="rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2.5"
                data-testid={`aset-item-${a.id}`}>
                <div className="flex items-center gap-3">
                  {a.jenis === "RUMAH_DINAS"
                    ? <Home className="h-4 w-4 text-rose-500 shrink-0" />
                    : <Building2 className="h-4 w-4 text-blue-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{a.nama}</p>
                    <p className="text-xs text-slate-500">
                      {a.jenis === "RUMAH_DINAS" ? "Rumah Dinas" : "Gedung"} · Indoor {fmtLuas(a.luas_indoor)} · Outdoor {fmtLuas(a.luas_outdoor)}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${KONDISI_META[a.kondisi] || ""} font-medium shrink-0`}>{a.kondisi}</Badge>
                  <button onClick={() => setHistoryId(historyId === a.id ? null : a.id)}
                    data-testid={`riwayat-aset-${a.id}`} title="Riwayat kondisi"
                    className={`h-7 w-7 rounded-md flex items-center justify-center transition-colors ${historyId === a.id ? "bg-teal-50 text-teal-600" : "text-slate-500 hover:bg-teal-50 hover:text-teal-600"}`}>
                    <History className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => openEdit(a)} data-testid={`edit-aset-${a.id}`}
                    className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(a)} data-testid={`delete-aset-${a.id}`}
                    className="h-7 w-7 rounded-md flex items-center justify-center text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <AsetFotoStrip aset={a}
                  onChanged={(updated) => setAsetList((l) => l.map((x) => (x.id === updated.id ? updated : x)))} />
                {historyId === a.id && (
                  <div className="mt-2 pl-7 space-y-1.5" data-testid={`aset-riwayat-${a.id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-teal-600">Riwayat Kondisi</p>
                    {(a.riwayat_kondisi || []).length ? (
                      [...a.riwayat_kondisi].reverse().map((r, i) => (
                        <div key={`${r.tanggal || i}-${r.kondisi_baru}`} className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          <span className="text-slate-400 w-24 shrink-0">
                            {r.tanggal ? formatTanggal(r.tanggal) : "-"}
                          </span>
                          {r.kondisi_lama ? (
                            <>
                              <Badge variant="outline" className={`${KONDISI_META[r.kondisi_lama] || ""} text-[10px] px-1.5 py-0`}>{r.kondisi_lama}</Badge>
                              <span>→</span>
                              <Badge variant="outline" className={`${KONDISI_META[r.kondisi_baru] || ""} text-[10px] px-1.5 py-0`}>{r.kondisi_baru}</Badge>
                            </>
                          ) : (
                            <>
                              <span>Kondisi awal:</span>
                              <Badge variant="outline" className={`${KONDISI_META[r.kondisi_baru] || ""} text-[10px] px-1.5 py-0`}>{r.kondisi_baru}</Badge>
                            </>
                          )}
                          <span className="text-slate-400">oleh {r.user_name || "Admin"}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-400">Belum ada riwayat kondisi (data lama). Riwayat mulai tercatat saat kondisi diubah.</p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
